'use client'

import React, { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useQuery } from 'convex/react'
import { CheckCircle, FileText, Loader2 } from 'lucide-react'

import { api } from '@/convex/_generated/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import ConsentGate from '@/components/booking/ConsentGate'
import StepActions from '@/components/booking/step-actions'
import type { BookingData } from '@/types/booking'

interface StepAppointmentSummaryProps {
  bookingData: BookingData
  isBooking: boolean
  onPrevious: () => void
  onBook: () => void
}

function labelForSlot(value?: string | null): string {
  if (!value) return '-'

  const match = value.match(/^(\d{2})_(\d{2})_(\d{2})_(\d{2})$/)

  if (match) {
    const [, sh, sm, eh, em] = match
    const start = new Date(2000, 0, 1, Number(sh), Number(sm))
    const end = new Date(2000, 0, 1, Number(eh), Number(em))

    return `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`
  }

  if (value === 'morning') return 'Morning'
  if (value === 'afternoon') return 'Afternoon'

  return value
}

const StepAppointmentSummary: React.FC<StepAppointmentSummaryProps> = ({
  bookingData,
  isBooking,
  onPrevious,
  onBook,
}) => {
  const [bookingAttempted, setBookingAttempted] = useState(false)
  const [showConsentGate, setShowConsentGate] = useState(false)
  const [consentSigned, setConsentSigned] = useState(false)

  const phoneDigits = useMemo(
    () => (bookingData.phone || '').replace(/\D/g, '').slice(-10),
    [bookingData.phone],
  )

  const existingConsent = useQuery(
    api.patientConsent.getByPhone,
    bookingData.phoneVerified && phoneDigits.length === 10
      ? {
          phone: phoneDigits,
          consentVersion: 'v1.0',
          patientConvexId: bookingData.existingPatientId
            ? (bookingData.existingPatientId as any)
            : undefined,
        }
      : 'skip',
  )

  const consentLookupLoading = existingConsent === undefined
  const hasConsent = !!existingConsent || consentSigned

  const handleBookClick = () => {
    setBookingAttempted(true)

    if (consentLookupLoading) return

    if (!hasConsent) {
      setShowConsentGate(true)
      return
    }

    onBook()
  }

  const canConfirmAfterSigning = showConsentGate && hasConsent && !isBooking

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Review your appointment
        </h2>
        <p className="mt-2 text-sm text-gray-600 sm:text-base">
          Confirm the details below before we create the booking.
        </p>
      </div>

      <Card className="border shadow-lg">
        <CardContent className="flex flex-col gap-5 px-4 py-5 sm:gap-6 sm:px-6 sm:py-6">
          <div className="rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-blue-50 p-4">
            <h3 className="flex items-center gap-2 text-base font-semibold text-green-900">
              <CheckCircle className="h-5 w-5 text-green-700" />
              Appointment Summary
            </h3>

            <div className="mt-4 grid gap-3 text-sm text-green-900">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                  Patient
                </p>
                <p className="font-semibold">
                  {bookingData.name || 'Unnamed patient'}
                </p>
                {bookingData.existingPatientHumanId && (
                  <p className="text-xs text-green-700">
                    ReACH ID: {bookingData.existingPatientHumanId}
                  </p>
                )}
              </div>

              <div className="grid gap-3 border-t border-green-100 pt-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                    Consultation
                  </p>
                  <p className="font-semibold">
                    {bookingData.flowType ?? 'Consultation'}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                    Clinician
                  </p>
                  <p className="font-semibold">
                    {bookingData.doctorName || '-'}
                  </p>
                  {bookingData.doctorSpecialty && (
                    <p className="text-xs text-green-700">
                      {bookingData.doctorSpecialty}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-3 border-t border-green-100 pt-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                    Date
                  </p>
                  <p className="font-semibold">
                    {bookingData.bookingDate
                      ? format(bookingData.bookingDate, 'EEEE, MMM d, yyyy')
                      : '-'}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                    Time
                  </p>
                  <p className="font-semibold">
                    {labelForSlot(bookingData.slot)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {bookingAttempted && consentLookupLoading && (
            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking consent status…
            </div>
          )}

          {showConsentGate && !hasConsent && (
            <ConsentGate
              phone={bookingData.phone}
              phoneVerified={!!bookingData.phoneVerified}
              patientName={bookingData.name}
              patientConvexId={bookingData.existingPatientId}
              patientHumanId={bookingData.existingPatientHumanId}
              onConsentStatusChange={setConsentSigned}
            />
          )}

          {canConfirmAfterSigning ? (
            <Button
              type="button"
              onClick={onBook}
              disabled={isBooking}
              className="w-full rounded-full bg-green-600 text-white hover:bg-green-700"
            >
              Confirm Booking
            </Button>
          ) : (
            <StepActions
              onPrevious={onPrevious}
              onNext={handleBookClick}
              nextDisabled={isBooking}
              previousLabel="Back"
              nextLabel={isBooking ? 'Booking…' : 'Book Appointment'}
              nextGradient="from-green-600 to-blue-600"
              className="pt-0"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default StepAppointmentSummary
